import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminThemes.styled";
import { adminThemeService } from "services/adminThemeService";
import { Theme } from "types";
import AdminThemeDrawer from "./AdminThemeDrawer";
import AdminThemeDetail from "./AdminThemeDetail";

const AdminThemes: React.FC = () => {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchThemes = useCallback(() => {
    const universityId = Number(localStorage.getItem("adminUniversityId"));
    setIsLoading(true);
    adminThemeService
      .getThemes(universityId)
      .then((data) => setThemes(data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const handleSuccess = () => {
    setIsDrawerOpen(false);
    fetchThemes();
  };

  const handleDelete = async (e: React.MouseEvent, themeId: number) => {
    e.stopPropagation();
    if (!window.confirm("테마를 삭제하시겠습니까?")) return;
    setDeletingId(themeId);
    try {
      await adminThemeService.deleteTheme(themeId);
      fetchThemes();
    } catch {
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = themes.filter((t) =>
    t.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <S.PageHeader>
        <S.PageTitle>테마 목록</S.PageTitle>
        <S.AddButton onClick={() => setIsDrawerOpen(true)}>+ 테마 추가</S.AddButton>
      </S.PageHeader>

      <S.Toolbar>
        <S.SearchWrap>
          <S.SearchIcon>🔍</S.SearchIcon>
          <S.SearchInput
            placeholder="테마명으로 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </S.SearchWrap>
        <S.CountText>
          {isLoading ? "로딩 중..." : `총 ${filtered.length}개 테마`}
        </S.CountText>
      </S.Toolbar>

      <S.Card>
        <S.Table>
          <S.Thead>
            <tr>
              <S.Th width="56px">#</S.Th>
              <S.Th>테마명</S.Th>
              <S.Th width="80px"></S.Th>
            </tr>
          </S.Thead>
          <S.Tbody>
            {!isLoading && filtered.length === 0 ? (
              <tr>
                <S.EmptyCell colSpan={3}>
                  {query
                    ? `"${query}"에 해당하는 테마가 없습니다.`
                    : "등록된 테마가 없습니다."}
                </S.EmptyCell>
              </tr>
            ) : (
              filtered.map((theme, index) => (
                <S.Tr
                  key={theme.id}
                  onClick={() => setSelectedTheme(theme)}
                  style={{ cursor: "pointer" }}
                >
                  <S.Td>
                    <S.IndexCell>{index + 1}</S.IndexCell>
                  </S.Td>
                  <S.Td>
                    <S.ThemeName>{theme.name}</S.ThemeName>
                  </S.Td>
                  <S.Td>
                    <S.DeleteButton
                      disabled={deletingId === theme.id}
                      onClick={(e) => handleDelete(e, theme.id)}
                    >
                      {deletingId === theme.id ? "삭제 중..." : "삭제"}
                    </S.DeleteButton>
                  </S.Td>
                </S.Tr>
              ))
            )}
          </S.Tbody>
        </S.Table>
      </S.Card>

      {isDrawerOpen && (
        <AdminThemeDrawer
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={handleSuccess}
        />
      )}

      {selectedTheme && (
        <AdminThemeDetail
          theme={selectedTheme}
          onClose={() => setSelectedTheme(null)}
          onUpdate={fetchThemes}
        />
      )}
    </>
  );
};

export default AdminThemes;
